import {
  createInitialData,
  getDraftPayload,
} from "@/containers/public/onboarding/helpers";
import type { DocumentKey } from "@/containers/public/onboarding/constants";
import { DOCUMENT_KEYS } from "@/containers/public/onboarding/constants";
import type { FormData } from "@/containers/public/onboarding/types";
import type { RentalsMerchantPortalRouterOutputs } from "@/trpc/client";

export type MerchantRequest =
  RentalsMerchantPortalRouterOutputs["merchantRequests"]["get"];
type MerchantRequestFields =
  RentalsMerchantPortalRouterOutputs["merchantRequests"]["template"];

/**
 * The public onboarding's payload. The backend puts it over the organization's
 * template on create, so a field left as prefilled keeps the template's value.
 */
export const getRequestPayload = (data: FormData) => {
  const payload = getDraftPayload(data);

  return {
    ...payload,
    billingInfo: {
      ...payload.billingInfo,
      city: payload.billingInfo.city || undefined,
    },
  };
};

/**
 * Fills the form from a saved request or, for a new one, from the template's
 * defaults. Mirrors the public wizard's hydration in `useOnboarding`.
 */
export const toFormData = (fields: MerchantRequestFields): FormData => {
  const defaults = createInitialData();
  const billing = fields.billingInfo;

  return {
    ...defaults,
    tipoPersona:
      billing?.documentType === "cc" ? "natural" : defaults.tipoPersona,
    nombre: billing?.name ?? "",
    nombreComercial: fields.displayName ?? "",
    identificacion: billing?.documentNumber ?? "",
    representanteLegalNombre: fields.legalRepresentative?.name ?? "",
    representanteLegalIdentificacion:
      fields.legalRepresentative?.documentNumber ?? "",
    direccion: billing?.address ?? "",
    ciudad: billing?.city ?? "",
    otraCiudad: billing?.otherCity ?? "",
    ciiu: fields.ciiu ?? "",
    web: fields.website ?? "",
    celular: fields.supportWhatsapp ?? defaults.celular,
    correoCliente: fields.supportEmail ?? defaults.correoCliente,
    erp: fields.erp ?? defaults.erp,
    otroErp: fields.otherErp ?? "",
    contratos:
      fields.contractCount !== undefined
        ? String(fields.contractCount)
        : defaults.contratos,
    cartera:
      fields.portfolioCop !== undefined
        ? String(fields.portfolioCop)
        : defaults.cartera,
    modeloRecaudo:
      fields.gateway === true
        ? "gateway"
        : fields.gateway === false
        ? "agregador"
        : defaults.modeloRecaudo,
    codigoPse: fields.pseServiceCode ?? "",
    banco: fields.merchantAccount?.bank ?? defaults.banco,
    tipoCuenta: fields.merchantAccount?.accountType ?? defaults.tipoCuenta,
    numeroCuenta:
      fields.merchantAccount?.accountNumber ?? defaults.numeroCuenta,
    titularCuentaEsComercio:
      fields.merchantAccount?.holderIsCompany ??
      defaults.titularCuentaEsComercio,
    titularCuentaNombre: fields.merchantAccount?.holder?.name ?? "",
    titularCuentaTipoDocumento:
      fields.merchantAccount?.holder?.documentType ?? "",
    titularCuentaNumeroDocumento:
      fields.merchantAccount?.holder?.documentNumber ?? "",
    palommaPlus: fields.palommaPlusEnabled ?? defaults.palommaPlus,
    correoFacturacion: billing?.email ?? defaults.correoFacturacion,
    whatsappFacturacion: billing?.phoneNumber ?? defaults.whatsappFacturacion,
    pep: fields.pep ? "si" : "no",
    cripto: fields.cryptoRelated ? "si" : "no",
    pepNombre: fields.pepDetails?.name ?? "",
    pepCargo: fields.pepDetails?.role ?? "",
    pepDetalle: fields.pepDetails?.notes ?? "",
    criptoActividad: fields.cryptoDetails?.activity ?? "",
    criptoRelacion: fields.cryptoDetails?.relationship ?? "",
    criptoDetalle: fields.cryptoDetails?.notes ?? "",
    origenFondos: fields.acceptedSourceOfFunds ?? false,
    tratamientoDatos: fields.acceptedDataPolicy ?? false,
    aceptaContrato: fields.acceptedContract ?? false,
  };
};

export const toUploadedDocs = (
  documents: MerchantRequest["documents"]
): Partial<Record<DocumentKey, string>> => {
  const uploaded: Partial<Record<DocumentKey, string>> = {};

  for (const key of DOCUMENT_KEYS) {
    const fileName = documents[key];
    if (fileName) uploaded[key] = fileName;
  }

  return uploaded;
};
